import { MouseEvent, ReactElement } from 'react';
import { CopyableText } from '@client/common/components/CopyableText';
import { StockCount } from '@client/common/components/StockCount';
import { isDefined } from '@client/common/utils/boolean/isDefined';
import { useTranslate } from '@client/i18n/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useOpenLink, useUrlCreator } from '@client/routes/hooks';
import { getCategoryUrlParams } from '@client/routes/utils/url';
import classNames from 'classnames';
import { Props } from './';
import './Details.scss';

export const Details = ({ isProductActive, product, className }: Props): ReactElement | null => {
  const t = useTranslate();
  const urlCreator = useUrlCreator();
  const { sku, manufacturer, category, settings } = product;
  const manufacturerSku = manufacturer?.id || null;
  const categoryName = category?.name || null;
  const categoryUrlParams = category?.url ? getCategoryUrlParams(category.url) : '';
  const categoryUrl = `${urlCreator(ROUTES.category)}${categoryUrlParams}`;
  const stockCount = isDefined(settings?.quantity) ? settings?.quantity : null;
  const quantityOrdered = settings?.quantityOrdered || null;
  const isSpecialOrder = !!settings?.isSpecialOrder;
  const openLink = useOpenLink();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    openLink(event, categoryUrl);
  };

  if (!sku && !manufacturerSku && !categoryName && !stockCount) {
    return null;
  }

  return (
    <div className={classNames('CartProductDetails', className)}>
      <table
        className={classNames(
          'cds-Table',
          'cds-Table--FeaturesSmall',
        )}
      >
        <tbody>
          <tr>
            <td className="cds-ProductBox-AttrLabel">{t('SKU')}:</td>
            <td className="cds-ProductBox-AttrValue">
              <CopyableText>{sku}</CopyableText>
            </td>
          </tr>

          {manufacturerSku
            ? (
              <tr>
                <td className="cds-ProductBox-AttrLabel">
                  {t('Manufacturer Product ID')}:
                </td>
                <td className="cds-ProductBox-AttrValue">
                  <CopyableText>{manufacturerSku}</CopyableText>
                </td>
              </tr>
            )
            : null
          }

          {categoryName
            ? (
              <tr>
                <td className="cds-ProductBox-AttrLabel">{t('Category')}:</td>
                <td className="cds-ProductBox-AttrValue">
                  {categoryUrl
                    ? (
                      <a
                        className="CartProductDetails-CategoryLink cds-Link"
                        href={categoryUrl}
                        onClick={handleClick}
                      >
                        {categoryName}
                      </a>
                    )
                    : categoryName
                  }
                </td>
              </tr>
            )
            : null
          }

          {(isProductActive && isDefined(stockCount)) && (
            <tr className="CartProductDetails-StockCount">
              <td className="cds-ProductBox-AttrLabel">{t('Stock')}:</td>
              <td className="cds-ProductBox-AttrValue">
                <StockCount
                  stockCount={stockCount}
                  quantityOrdered={quantityOrdered}
                  isSpecialOrder={isSpecialOrder}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
